/**
 * Deck export format registry
 *
 * Single source of truth for the deck export formats offered by
 * `ExportDeckModal`: labels, file metadata, which options each format
 * supports, per-format option defaults, and the exporter dispatch.
 */
import { getFullTypeLine } from '@/lib/cardUtils';
import type { Currency } from '@/types/card';
import type { Deck, EnrichedDeckCard } from '@/types/deck';

import { exportDeckForAi } from './ai';
import { exportMarkdownSummary, exportTextSummary, type SummaryExportCard } from './markdown';
import { exportDeckListArena } from './mtga';
import { exportSingleDeckBackup } from './mtgcraft';
import { exportDeckListMtgo } from './mtgo';
import { exportDeckList } from './text';

// ============================================================================
// Types
// ============================================================================

export type DeckExportFormat = 'mtgcraft' | 'mtga' | 'moxfield' | 'mtgo' | 'arena' | 'text-summary' | 'markdown-summary' | 'ai';

export interface DeckExportOptionsState {
  includeSetInfo: boolean;
  includeMaybeboard: boolean;
  verbose: boolean;
  includeOracleText: boolean;
}

export interface DeckExportContext {
  deck: Deck;
  /** Deck name used in headers/filenames (may override `deck.name`) */
  deckName: string;
  cards: EnrichedDeckCard[];
  options: DeckExportOptionsState;
  currency: Currency;
}

export interface DeckExportFormatMeta {
  value: DeckExportFormat;
  label: string;
  description: string;
  /** Format segment of the generated filename */
  fileLabel: string;
  extension: string;
  mimeType: string;
  /** Options rendered for this format; all others are hidden */
  supportedOptions: (keyof DeckExportOptionsState)[];
  /** Option values applied when this format is selected */
  defaults: Partial<DeckExportOptionsState>;
  run: (ctx: DeckExportContext) => string;
}

// ============================================================================
// Option defaults
// ============================================================================

export const DECK_EXPORT_BASE_OPTIONS: DeckExportOptionsState = {
  includeSetInfo: true,
  includeMaybeboard: false,
  verbose: true,
  includeOracleText: false,
};

// ============================================================================
// Helpers
// ============================================================================

/** Summary exporters group by type line, which lives on the card for enriched entries */
function toSummaryCards(ctx: DeckExportContext): SummaryExportCard[] {
  return ctx.cards
    .filter((c) => c.zone !== 'maybeboard' || ctx.options.includeMaybeboard)
    .map((c) => ({ ...c, type_line: getFullTypeLine(c.card) ?? '' }));
}

// ============================================================================
// Registry
// ============================================================================

export const DECK_EXPORT_FORMATS: DeckExportFormatMeta[] = [
  {
    value: 'mtgcraft',
    label: 'MtgCraft Backup',
    description: 'Full JSON backup with all metadata',
    fileLabel: 'MtgCraft',
    extension: 'json',
    mimeType: 'application/json',
    supportedOptions: [],
    defaults: {},
    run: ({ deck, cards }) =>
      exportSingleDeckBackup(
        deck,
        cards.map((c) => ({ id: c.id, scryfall_id: c.scryfall_id, quantity: c.quantity, zone: c.zone })),
      ),
  },
  {
    value: 'mtga',
    label: 'MTGA Text',
    description: 'Compatible with MTG Arena',
    fileLabel: 'MTGA',
    extension: 'txt',
    mimeType: 'text/plain',
    supportedOptions: ['includeSetInfo', 'includeMaybeboard'],
    defaults: {},
    run: ({ cards, deckName, options }) =>
      exportDeckList(cards, deckName, {
        includeSetInfo: options.includeSetInfo,
        includeMaybeboard: options.includeMaybeboard,
        includeTokens: true,
        format: 'mtga',
      }),
  },
  {
    value: 'moxfield',
    label: 'Moxfield',
    description: 'Import into Moxfield decks',
    fileLabel: 'Moxfield',
    extension: 'txt',
    mimeType: 'text/plain',
    supportedOptions: ['includeSetInfo', 'includeMaybeboard'],
    defaults: {},
    run: ({ cards, deckName, options }) =>
      exportDeckList(cards, deckName, {
        includeSetInfo: options.includeSetInfo,
        includeMaybeboard: options.includeMaybeboard,
        includeTokens: true,
        format: 'moxfield',
      }),
  },
  {
    value: 'mtgo',
    label: 'MTGO',
    description: 'Magic Online deck format',
    fileLabel: 'MTGO',
    extension: 'txt',
    mimeType: 'text/plain',
    supportedOptions: ['includeSetInfo', 'includeMaybeboard'],
    defaults: {},
    run: ({ cards, deckName, options }) =>
      exportDeckListMtgo(cards, deckName, {
        includeSetInfo: options.includeSetInfo,
        includeMaybeboard: options.includeMaybeboard,
      }),
  },
  {
    value: 'arena',
    label: 'Arena',
    description: 'MTG Arena with About/Name preamble',
    fileLabel: 'Arena',
    extension: 'txt',
    mimeType: 'text/plain',
    supportedOptions: ['includeMaybeboard'],
    defaults: {},
    run: ({ cards, deckName, options }) => exportDeckListArena(cards, deckName, { includeMaybeboard: options.includeMaybeboard }),
  },
  {
    value: 'text-summary',
    label: 'Text Summary',
    description: 'Grouped by type, importable format',
    fileLabel: 'Summary',
    extension: 'txt',
    mimeType: 'text/plain',
    supportedOptions: ['includeMaybeboard'],
    defaults: {},
    run: (ctx) => exportTextSummary(toSummaryCards(ctx), { deck: ctx.deck }),
  },
  {
    value: 'markdown-summary',
    label: 'Markdown Summary',
    description: 'For Reddit, Discord, forums',
    fileLabel: 'Markdown',
    extension: 'md',
    mimeType: 'text/markdown',
    supportedOptions: ['includeMaybeboard'],
    defaults: {},
    run: (ctx) => exportMarkdownSummary(toSummaryCards(ctx), { deck: ctx.deck }),
  },
  {
    value: 'ai',
    label: 'AI Chat',
    description: 'For pasting into ChatGPT/Claude for deckbuilding advice',
    fileLabel: 'AI Chat',
    extension: 'md',
    mimeType: 'text/markdown',
    supportedOptions: ['verbose', 'includeOracleText', 'includeMaybeboard'],
    defaults: { verbose: true, includeOracleText: false, includeMaybeboard: true },
    run: ({ deck, cards, options, currency }) =>
      exportDeckForAi(deck, cards, {
        verbose: options.verbose,
        includeOracleText: options.includeOracleText,
        includeMaybeboard: options.includeMaybeboard,
        currency,
      }),
  },
];

// ============================================================================
// Dispatch
// ============================================================================

export function getDeckExportFormat(format: DeckExportFormat): DeckExportFormatMeta {
  const meta = DECK_EXPORT_FORMATS.find((f) => f.value === format);
  if (!meta) throw new Error(`Unknown deck export format: ${format}`);
  return meta;
}

/** Option state for a format: base defaults overridden by the format's defaults */
export function getDeckExportDefaults(format: DeckExportFormat): DeckExportOptionsState {
  return { ...DECK_EXPORT_BASE_OPTIONS, ...getDeckExportFormat(format).defaults };
}

export function exportDeck(format: DeckExportFormat, ctx: DeckExportContext): string {
  return getDeckExportFormat(format).run(ctx);
}
